import type { VerseTopic } from "./types";

export const topic: VerseTopic = {
  slug: "bible-verses-about-grief",
  title: "10 Bible verses about grief, without the tidying up",
  h1: "Bible verses about grief",
  description:
    "Ten passages from the World English Bible for loss and mourning, with a note on each — including the two that are usually quoted to end a conversation rather than sit in one.",
  targets: [
    "bible verses about grief",
    "scripture for loss and mourning",
    "comforting verses after a death",
  ],
  published: "2026-08-27",
  standfirst:
    "Scripture does not treat grief as a problem to be solved quickly. Two of the most-quoted verses in this category are routinely used to do exactly that.",
  takeaways: [
    "1 Thessalonians 4:13 does not say do not grieve — it says do not grieve as those who have no hope, which is a comparison rather than a prohibition.",
    "The shortest verse in most English Bibles is Jesus weeping at a graveside he was about to reverse, which rules out the idea that grief is unbelief.",
    "Ecclesiastes 3:4 gives mourning its own season, and Romans 12:15 tells the people around a griever what to do: weep with them, not explain to them.",
    "Revelation 21:4 is true and it is not yet — quoting a future without acknowledging the present is the commonest way these verses land badly.",
  ],
  intro: [
    {
      type: "p",
      text: "Most pages of grief verses are written to comfort quickly, and most people carrying a real loss have already been comforted quickly several times, usually by somebody who meant it kindly. The effect is often the opposite of the intention: a true sentence arriving too early reads as an instruction to stop, and the person stops talking rather than stops hurting.",
    },
    {
      type: "p",
      text: "So the notes below try to say when each passage is for. Some of these are for the first fortnight and some are emphatically not; two of the best known are usually deployed as conversation-enders and are much better than that when they are read whole. Where a verse is commonly misused in this way, the note says so plainly rather than passing it on.",
    },
    {
      type: "note",
      text: "This is a page of passages, not counsel. Grief that is not moving after a long time, or that comes with a wish not to be here, is a reason to talk to a doctor or a pastor — and asking for that help is not a failure of faith.",
    },
  ],
  verses: [
    {
      verse: {
        text: "Yahweh is near to those who have a broken heart, and saves those who have a crushed spirit.",
        ref: "Psalm 34:18",
      },
      note: "The claim is proximity rather than repair, and it is the right verse for the first week because it asks nothing. Note that both descriptions are physical — broken, crushed — which matches what grief actually does to a body, and the psalm does not treat either as a spiritual failing to be corrected.",
    },
    {
      verse: {
        text: "Jesus wept.",
        ref: "John 11:35",
      },
      note: "Two words, at a graveside, by someone who knew he was about to raise the man and wept anyway. That last detail is the whole argument: the outcome being certain did not make the grief inappropriate. Anyone who has been told that tears show a lack of faith has been told something this verse rules out in two words.",
    },
    {
      verse: {
        text: "Blessed are those who mourn, for they shall be comforted.",
        ref: "Matthew 5:4",
      },
      note: "The blessing is on the mourners while mourning, not on the ones who have finished. And the comfort is in the future tense — shall be — which is honest about the interval. This is one of the few sentences in the New Testament that describes grief as a state one can be blessed inside rather than rescued from.",
    },
    {
      verse: {
        text: "But we don't want you to be ignorant, brothers, concerning those who have fallen asleep, so that you don't grieve like the rest, who have no hope.",
        ref: "1 Thessalonians 4:13",
      },
      note: "Read carelessly this becomes do not grieve, and it is quoted that way constantly. What it says is do not grieve LIKE those with no hope — a comparison of manner, not a prohibition of the thing. Paul assumes the grief and addresses its character. The difference between those two readings is, in practice, whether a bereaved person feels accompanied or corrected.",
    },
    {
      verse: {
        text: "He heals the broken in heart, and binds up their wounds.",
        ref: "Psalm 147:3",
      },
      note: "Binding up is slow, physical, repeated work — the language of dressing a wound rather than of removing one. The image concedes that there is an injury and that it will take time, which is more use than a promise of instant relief and considerably more like what people report.",
    },
    {
      verse: {
        text: "Blessed be … the Father of mercies and God of all comfort, who comforts us in all our affliction, that we may be able to comfort those who are in any affliction, through the comfort with which we ourselves are comforted by God.",
        ref: "2 Corinthians 1:3–4",
      },
      note: "The sentence loops on purpose: comfort received becomes comfort given. It is not a reason for the loss and does not offer one — it describes what can eventually be done with what was learned. Best kept for much later; offered early it sounds like an explanation, and there isn't one.",
    },
    {
      verse: {
        text: "It is because of Yahweh's loving kindnesses that we are not consumed, because his mercies don't fail. They are new every morning.",
        ref: "Lamentations 3:22–23",
      },
      note: "Sitting inside a book of funeral poetry for a destroyed city, in a chapter where the writer has just said his soul is bowed down within him. The famous line is not an interruption of the lament; it is spoken from the middle of one, which is exactly why it can be said to somebody who is not finished grieving.",
    },
    {
      verse: {
        text: "A time to weep, and a time to laugh; a time to mourn, and a time to dance.",
        ref: "Ecclesiastes 3:4",
      },
      note: "Mourning is given a season of its own, with no ranking against the others. This is the passage to hold when someone implies you should have moved on by now — the text does not treat weeping as a failure of the dancing season, and it does not put a length on either.",
    },
    {
      verse: {
        text: "Rejoice with those who rejoice. Weep with those who weep.",
        ref: "Romans 12:15",
      },
      note: "The instruction to everyone else, and the most useful verse on this page for anyone who is not the one grieving. The command is to match, not to explain, fix or reframe. Almost every well-meant thing that lands badly at a funeral is a substitution of explanation for the second half of this sentence.",
    },
    {
      verse: {
        text: "He will wipe away every tear from their eyes. Death will be no more; neither will there be mourning, nor crying, nor pain any more.",
        ref: "Revelation 21:4",
      },
      note: "True, and future. Quoted early it functions as a request to stop crying now, which is not what it says — the tears are still there in the sentence, and somebody has to come and wipe them. Read in its own tense it is not a tidying-up; it is a claim that the present state is temporary, made without denying that it is the present state.",
    },
  ],
  pray: [
    {
      type: "h2",
      id: "what-to-say",
      text: "If you are the one trying to help",
    },
    {
      type: "p",
      text: "Romans 12:15 is the whole instruction and it is harder than it looks, because the impulse to explain is strong and it is almost always about the discomfort of the person explaining. Weep with those who weep does not require you to have anything to say. Turning up repeatedly, over months rather than during the first week, is worth more than any verse handed over at the funeral.",
    },
    {
      type: "p",
      text: "If you do offer a passage, offer Psalm 34:18 or John 11:35 rather than Revelation 21:4 or 2 Corinthians 1:3–4. The first two ask nothing of the person; the second two are true and have a job to do much later, and arriving early they read as instructions to hurry. {The-collection-for-the-hours-when-the-worry-is-loudest} is the one to reach for if the nights have become the worst part, which for most grieving people they are.",
      links: {
        "The-collection-for-the-hours-when-the-worry-is-loudest":
          "/verses/bible-verses-for-anxiety-at-night",
      },
    },
    {
      type: "h2",
      id: "a-prayer-in-grief",
      text: "A prayer for the middle of it",
    },
    {
      type: "p",
      text: "Lord, you are near to the broken-hearted and I am asking you to be near. I am not asking to be finished with this. Bind it up slowly, as you do. Be with me in the mornings, which are the worst part, and let the people around me weep with me rather than explain. Amen.",
    },
    {
      type: "note",
      text: "Grief and sleeplessness travel together, and the nights are usually the hardest part. The collection for those hours is the neighbour of this one.",
    },
  ],
  faq: [
    {
      q: "Does the Bible say Christians should not grieve?",
      a: "No. 1 Thessalonians 4:13 says not to grieve like those who have no hope — a comparison of manner, not a prohibition. Jesus wept at a graveside he was about to reverse, and Ecclesiastes gives mourning a season of its own. The idea that grief indicates weak faith is not in these texts.",
    },
    {
      q: "What is a good Bible verse to send someone who is grieving?",
      a: "Psalm 34:18 or John 11:35, early on — both ask nothing of the person and neither implies they should feel better yet. Revelation 21:4 and 2 Corinthians 1:3–4 are true and are better later; sent in the first weeks they tend to read as a request to stop.",
    },
    {
      q: "How long should grief last?",
      a: "Scripture puts no length on it and gives mourning its own season. If grief has stopped moving at all after a long time, or arrives with a wish not to be here, that is a reason to speak to a doctor or a pastor — and doing so is not a spiritual failure.",
    },
  ],
  related: [
    "how-to-stop-scrolling-at-night",
    "morning-quiet-time-routine",
    "digital-sabbath",
  ],
  relatedTopics: [
    "bible-verses-for-anxiety-at-night",
    "bible-verses-about-peace",
  ],
};
